import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Grid,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    styled,
    Button,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import useCompanySettings from '../hooks/useCompanySettings'; // Merkezi şirket ayarlarını ekledik
import { pricingTable } from './Payment'; // Paket limiti kontrolü için pricingTable'ı import et
import { useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
}));

function RfidCardsPage() {
    const [rfidCards, setRfidCards] = useState([]);
    const [newCardId, setNewCardId] = useState('');
    const [selectedCardId, setSelectedCardId] = useState('');
    const [updateCardId, setUpdateCardId] = useState('');
    const [deleteCardId, setDeleteCardId] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [pendingActionData, setPendingActionData] = useState(null);
    const [selectedCardDetails, setSelectedCardDetails] = useState(null);
    const [isGetCardDetailsOpen, setIsGetCardDetailsOpen] = useState(false);
    const [rfidLimit, setRfidLimit] = useState(null);

    const apiBaseUrl = 'https://pdks.bytebridge.com.tr/api';
    const settings = useCompanySettings(); // Merkezi şirket ayarlarını kullanmak için settings değişkenini tanımladık
    const navigate = useNavigate();

    useEffect(() => {
        fetchRfidCards();
        axios.get('https://pdks.bytebridge.com.tr/api-validity?port=5001')
            .then(res => {
                const lim = res.data.rfid_count || res.data.rfidCount || res.data.RfidCount || res.data[0]?.rfid_count || 0;
                setRfidLimit(Number(lim));
            })
            .catch(() => setRfidLimit(null));
    }, []);

    const fetchRfidCards = () => {
        axios.get(`${apiBaseUrl}/rfidcard`)
            .then(response => setRfidCards(response.data))
            .catch(err => {
                console.error('RFID kartları alınırken hata oluştu:', err);
                setError('RFID kartları alınırken bir hata oluştu.');
                setSnackbarSeverity('error');
                setSnackbarMessage('RFID kartları alınırken bir hata oluştu.');
                setSnackbarOpen(true);
            });
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setMessage('');
        setError('');
        if (name === 'newCardId') {
            setNewCardId(value);
        } else if (name === 'selectedCardId') {
            setSelectedCardId(value);
        } else if (name === 'updateCardId') {
            setUpdateCardId(value);
        } else if (name === 'deleteCardId') {
            setDeleteCardId(value);
        }
    };

    const showSnackbar = (severity, msg) => {
        setSnackbarSeverity(severity);
        setMessage('');
        setError('');
        setSnackbarMessage(msg);
        if (severity === 'success') {
            setMessage(msg);
        } else {
            setError(msg);
        }
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const handleOpenConfirmDialog = (action, data = null) => {
        setPendingAction(action);
        setPendingActionData(data);
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
        setPendingAction(null);
        setPendingActionData(null);
    };

    const handleConfirmAction = () => {
        if (pendingAction === 'add') {
            handleAddCardConfirmed();
        } else if (pendingAction === 'update') {
            handleUpdateCardConfirmed();
        } else if (pendingAction === 'delete') {
            handleDeleteCardConfirmed();
        }
        handleCloseConfirmDialog();
    };

    const handleAddCard = () => {
        if (!newCardId) {
            showSnackbar('warning', 'Lütfen bir kart ID girin.');
            return;
        }
        if (isRfidLimitExceeded()) {
            showSnackbar('error', 'Kart limiti aşıldı. Lütfen paketinizi yükseltin.');
            return;
        }
        handleOpenConfirmDialog('add', { card_id: newCardId });
    };

    const handleAddCardConfirmed = () => {
        axios.post(`${apiBaseUrl}/rfidcards`, { card_id: newCardId })
            .then(response => {
                showSnackbar('success', response.data.message);
                setNewCardId('');
                fetchRfidCards();
            })
            .catch(err => {
                console.error('RFID kartı eklenirken hata oluştu:', err);
                showSnackbar('error', err.response?.data?.message || 'RFID kartı eklenirken bir hata oluştu.');
            });
    };

    const handleGetCard = () => {
        if (!selectedCardId) {
            showSnackbar('warning', 'Lütfen bir kart ID girin.');
            return;
        }
        // GET ile kart detaylarını almak için query param kullan
        axios.get(`${apiBaseUrl}/rfidcard`)
            .then(response => {
                // Tüm kartlar içinde aranan kartı bul
                const card = response.data.find(c => c.card_id === selectedCardId);
                if (card) {
                    setSelectedCardDetails(card);
                    setIsGetCardDetailsOpen(true);
                } else {
                    showSnackbar('error', 'RFID kart detayları bulunamadı.');
                    setSelectedCardDetails(null);
                    setIsGetCardDetailsOpen(false);
                }
            })
            .catch(err => {
                console.error('RFID kart detayları alınırken hata oluştu:', err);
                showSnackbar('error', err.response?.data?.message || 'RFID kart detayları bulunamadı.');
                setSelectedCardDetails(null);
                setIsGetCardDetailsOpen(false);
            });
    };

    const handleCloseGetCardDetails = () => {
        setIsGetCardDetailsOpen(false);
        setSelectedCardDetails(null);
        setSelectedCardId('');
    };

    const handleUpdateCard = () => {
        if (!selectedCardId || !updateCardId) {
            showSnackbar('warning', 'Lütfen güncellenecek kart ID ve yeni kart ID girin.');
            return;
        }
        handleOpenConfirmDialog('update', { selectedCardId, updateCardId });
    };

    const handleUpdateCardConfirmed = () => {
        axios.put(`${apiBaseUrl}/rfidcard`, { 
            card_id: pendingActionData.selectedCardId,
            new_card_id: pendingActionData.updateCardId 
        })
            .then(response => {
                showSnackbar('success', response.data.message);
                setSelectedCardId('');
                setUpdateCardId('');
                fetchRfidCards();
            })
            .catch(err => {
                console.error('RFID kartı güncellenirken hata oluştu:', err);
                showSnackbar('error', err.response?.data?.message || 'RFID kartı güncellenirken bir hata oluştu.');
            });
    };

    const handleDeleteCard = () => {
        if (!deleteCardId) {
            showSnackbar('warning', 'Lütfen silinecek kart ID girin.');
            return;
        }
        handleOpenConfirmDialog('delete', { deleteCardId });
    };

    const handleDeleteCardConfirmed = () => {
        axios.delete(`${apiBaseUrl}/rfidcard`, { 
            data: { card_id: pendingActionData.deleteCardId }
        })
            .then(response => {
                showSnackbar('success', response.data.message);
                setDeleteCardId('');
                fetchRfidCards();
            })
            .catch(err => {
                console.error('RFID kartı silinirken hata oluştu:', err);
                showSnackbar('error', err.response?.data?.message || 'RFID kartı silinirken bir hata oluştu.');
            });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Paket limiti kontrol fonksiyonu
    const isRfidLimitExceeded = () => {
        if (rfidLimit == null || !rfidCards) return false;
        return rfidCards.length >= rfidLimit;
    };

    // EXCEL AKTARIMI
    const handleExportExcel = () => {
        if (!rfidCards || rfidCards.length === 0) {
            alert('Aktarılacak RFID kartı bulunamadı!');
            return;
        }
        const excelRows = [
            ['Kart ID', 'Eklenme Tarihi', 'Durum', 'Açıklama']
        ];
        rfidCards.forEach(card => {
            excelRows.push([
                card.card_id || card.id || '',
                card.created_at ? new Date(card.created_at).toLocaleString('tr-TR', { timeZone: 'UTC' }) : '',
                card.status || '',
                card.description || ''
            ]);
        });
        const ws = XLSX.utils.aoa_to_sheet(excelRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'RFID Kartları');
        XLSX.writeFile(wb, `rfid_kartlari_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    const handleLimitWarningClick = () => {
        navigate('/payment');
    };

    return (
        <Box sx={{ flexGrow: 1, padding: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <Tooltip title="RFID kart işlemlerini bu sayfadan yönetebilirsiniz.">
                    <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
                </Tooltip>
                <Typography variant="h5">{settings?.companyName || 'Şirket Adı'} - Kayıtlı RFID Kartları</Typography>
            </div>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
                <Button variant="outlined" color="success" onClick={handleExportExcel}>
                    Kayıtlı RFID Kartları Excel'e Aktar
                </Button>
            </Box>

            <StyledPaper elevation={3}>
                <Typography variant="h6" gutterBottom>
                    RFID Kart Listesi
                </Typography>
                <TableContainer>
                    <Table aria-label="rfid cards table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Kart ID</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rfidCards.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((card) => (
                                <TableRow key={card.card_id}>
                                    <TableCell component="th" scope="row">
                                        {card.card_id}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={rfidCards.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </StyledPaper>

            <StyledPaper elevation={3}>
                <Typography variant="h6" gutterBottom>
                    RFID Kart İşlemleri
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label="Yeni Kart ID"
                            variant="outlined"
                            name="newCardId"
                            value={newCardId}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                        />
                        <Button variant="contained" color="primary" onClick={handleAddCard} fullWidth>
                            Ekle
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label="Kart ID"
                            variant="outlined"
                            name="selectedCardId"
                            value={selectedCardId}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                        />
                        <Button variant="contained" color="info" onClick={handleGetCard} fullWidth>
                            Detayları Getir
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label="Silinecek Kart ID"
                            variant="outlined"
                            name="deleteCardId"
                            value={deleteCardId}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                        />
                        <Button variant="contained" color="error" onClick={handleDeleteCard} fullWidth>
                            Sil
                        </Button>
                    </Grid>
                </Grid>
            </StyledPaper>

            <StyledPaper elevation={3}>
                <Typography variant="h6" gutterBottom>
                    RFID Kart Güncelle
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label="Güncellenecek Kart ID"
                            variant="outlined"
                            name="selectedCardId"
                            value={selectedCardId}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label="Yeni Kart ID"
                            variant="outlined"
                            name="updateCardId"
                            value={updateCardId}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Button variant="contained" color="warning" onClick={handleUpdateCard} fullWidth>
                            Güncelle
                        </Button>
                    </Grid>
                </Grid>
            </StyledPaper>

            {/* Bildirim (Toast) */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: '100%', cursor: snackbarMessage.includes('limiti aşıldı') ? 'pointer' : 'default' }}
                    onClick={snackbarMessage.includes('limiti aşıldı') ? handleLimitWarningClick : undefined}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* Onay Diyaloğu */}
            {confirmDialogOpen && (
                <Dialog
                    open={confirmDialogOpen}
                    onClose={handleCloseConfirmDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">
                        {pendingAction === 'add' && 'Yeni Kart Ekleme Onayı'}
                        {pendingAction === 'update' && 'Kart Güncelleme Onayı'}
                        {pendingAction === 'delete' && 'Kart Silme Onayı'}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            {pendingAction === 'add' &&
                                `"${pendingActionData?.card_id}" ID'li yeni kartı eklemek istediğinize emin misiniz?`}
                            {pendingAction === 'update' &&
                                `"${pendingActionData?.selectedCardId}" ID'li kartın ID'sini "${pendingActionData?.updateCardId}" olarak güncellemek istediğinize emin misiniz?`}
                            {pendingAction === 'delete' &&
                                `"${pendingActionData?.deleteCardId}" ID'li kartı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseConfirmDialog}>İptal</Button>
                        <Button onClick={handleConfirmAction} autoFocus>
                            Onayla
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* RFID Kart Detayları Diyaloğu */}
            <Dialog
                open={isGetCardDetailsOpen}
                onClose={handleCloseGetCardDetails}
                aria-labelledby="rfid-card-details-title"
                aria-describedby="rfid-card-details-description"
            >
                <DialogTitle id="rfid-card-details-title">RFID Kart Detayları</DialogTitle>
                <DialogContent>
                    {selectedCardDetails ? (
                        <TableContainer>
                            <Table aria-label="rfid card details">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Alan</StyledTableCell>
                                        <StyledTableCell>Değer</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow key="id">
                                        <TableCell component="th" scope="row">
                                            ID
                                        </TableCell>
                                        <TableCell>{selectedCardDetails.id}</TableCell>
                                    </TableRow>
                                    <TableRow key="card_id">
                                        <TableCell component="th" scope="row">
                                            Kart ID
                                        </TableCell>
                                        <TableCell>{selectedCardDetails.card_id}</TableCell>
                                    </TableRow>
                                    <TableRow key="employee_id">
                                        <TableCell component="th" scope="row">
                                            Çalışan ID
                                        </TableCell>
                                        <TableCell>{selectedCardDetails.employee_id}</TableCell>
                                    </TableRow>
                                    <TableRow key="employee_name">
                                        <TableCell component="th" scope="row">
                                            Çalışan Adı
                                        </TableCell>
                                        <TableCell>{selectedCardDetails.employee_name}</TableCell>
                                    </TableRow>
                                    <TableRow key="issue_date">
                                        <TableCell component="th" scope="row">
                                            Verilme Tarihi
                                        </TableCell>
                                        <TableCell>{selectedCardDetails.issue_date}</TableCell>
                                    </TableRow>
                                    <TableRow key="status">
                                        <TableCell component="th" scope="row">
                                            Durum
                                        </TableCell>
                                        <TableCell>{selectedCardDetails.status}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography>Kart detayları yükleniyor...</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseGetCardDetails}>Kapat</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default RfidCardsPage;